/**
 * Chronological activity feed with type filters.
 */
import { useState } from "react";
import { Button, Form, ListGroup } from "react-bootstrap";
import { Link } from "react-router";
import type { Params } from "../lib/api.ts";
import { useFormat } from "../lib/format.ts";
import { useApi } from "../lib/query.ts";
import { Icon } from "./ui/Icon.tsx";
import { Money, QueryState } from "./ui/kit.tsx";

interface TimelineItem {
  type: string;
  id: string;
  occurredAt: string;
  propertyId: string | null;
  tenancyId: string | null;
  title: string;
  detail: string | null;
  amountPence: number | null;
  subtype: string | null;
  addressLine1: string | null;
}

const TYPES: Array<{ value: string; label: string; icon: string; route: string }> = [
  { value: "income", label: "Income", icon: "income", route: "income" },
  { value: "expense", label: "Expenses", icon: "expense", route: "expenses" },
  { value: "task", label: "Tasks", icon: "tasks", route: "tasks" },
  { value: "communication", label: "Communications", icon: "message", route: "communications" },
  { value: "maintenance", label: "Maintenance", icon: "tool", route: "maintenance" },
  { value: "document", label: "Documents", icon: "file", route: "documents" },
  { value: "compliance", label: "Compliance", icon: "shield", route: "compliance" },
  { value: "tenancy", label: "Tenancies", icon: "key", route: "tenancies" },
  { value: "rent", label: "Rent changes", icon: "income", route: "tenancies" },
];

function linkFor(item: TimelineItem): string {
  if (item.type === "rent") return `/tenancies/${item.tenancyId}`;
  const route = TYPES.find((t) => t.value === item.type)?.route ?? "";
  return `/${route}/${item.id}`;
}

export function Timeline({
  filters,
  limit = 25,
  showFilters = true,
  showProperty = true,
}: {
  filters: Params;
  limit?: number;
  showFilters?: boolean;
  showProperty?: boolean;
}) {
  const f = useFormat();
  const [types, setTypes] = useState<string[]>([]);
  const [pages, setPages] = useState(1);
  const q = useApi<TimelineItem[]>(
    "/api/timeline",
    { ...filters, types: types.join(","), limit: limit * pages },
    { keepPrevious: true },
  );
  return (
    <>
      {showFilters && (
        <div className="d-flex flex-wrap gap-3 mb-3 small">
          {TYPES.map((t) => (
            <Form.Check
              key={t.value}
              inline
              type="checkbox"
              id={`tl-${t.value}`}
              label={t.label}
              checked={types.includes(t.value)}
              onChange={(e) =>
                setTypes(e.target.checked ? [...types, t.value] : types.filter((x) => x !== t.value))
              }
            />
          ))}
        </div>
      )}
      <QueryState query={q}>
        {(items) =>
          items.length === 0 ? (
            <p className="text-muted small mb-0">No activity yet.</p>
          ) : (
            <>
              <ListGroup variant="flush">
                {items.map((item) => {
                  const type = TYPES.find((t) => t.value === item.type);
                  return (
                    <ListGroup.Item
                      key={`${item.type}-${item.id}-${item.occurredAt}`}
                      className="d-flex gap-3 px-0"
                    >
                      <span className="text-muted pt-1">
                        <Icon name={type?.icon ?? "activity"} size={16} />
                      </span>
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex justify-content-between gap-2">
                          <Link to={linkFor(item)} className="fw-semibold text-truncate">
                            {item.title}
                          </Link>
                          {item.amountPence !== null && (
                            <Money
                              pence={item.amountPence}
                              kind={
                                item.type === "income"
                                  ? "income"
                                  : item.type === "expense" || item.type === "maintenance"
                                    ? "expense"
                                    : undefined
                              }
                            />
                          )}
                        </div>
                        <div className="small text-muted text-truncate">
                          {[item.detail, showProperty ? item.addressLine1 : null].filter(Boolean).join(" · ")}
                        </div>
                        <div className="small text-muted">{f.date(item.occurredAt)}</div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              {items.length >= limit * pages && (
                <div className="text-center mt-2">
                  <Button size="sm" variant="link" onClick={() => setPages(pages + 1)}>
                    Load more
                  </Button>
                </div>
              )}
            </>
          )
        }
      </QueryState>
    </>
  );
}
